import type { DayEntry, MemberId } from '../contract.js';
import { copy, kindWord } from '../copy.js';
import { dateLong, dayNumber, gridPos } from '../dates.js';
import { popRanks } from '../hash.js';
import { dayStatus, entriesOf, isYou, memberOf, todayOf } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import { color, shadow } from '../tokens.js';
import { pfpOfMember } from './picture.js';
import type { Pfp } from './picture.js';

/** The day layer, ported from `dayVals` (02 §3.3–3.6; 03 §3.2–3.4). */

export interface DayCell { readonly x: number; readonly y: number; readonly op: number; readonly tr: string }
export interface DaySquare {
  readonly id: MemberId;
  readonly x: number; readonly y: number;
  /** Outline colour: the member's, or white when hovered. */
  readonly outline: string;
  readonly shadow: string;
  readonly filter: string;
  readonly sheenTf: string; readonly sheenTr: string;
  readonly picture: Pfp;
  readonly op: number; readonly tf: string; readonly tr: string;
  readonly aria: string;
}
export interface DayMessage { readonly text: string; readonly pulsing: boolean; readonly retry: boolean; readonly op: number }
export interface DayVals {
  readonly show: boolean;
  readonly cells: readonly DayCell[];
  readonly squares: readonly DaySquare[];
  readonly message: DayMessage | null;
  /** The posts shown (empty until the day has loaded) and where their squares sit. */
  readonly entries: readonly DayEntry[];
  readonly layout: readonly { readonly x: number; readonly y: number }[];
}

const pop = (ms: number): string => `opacity 140ms ease ${ms}ms, transform 320ms cubic-bezier(.3,1.6,.5,1) ${ms}ms`;

export function dayVals(st: AppState, reduced: boolean): DayVals {
  const date = st.date, status = dayStatus(st, date), loaded = status === 'loaded', entries = entriesOf(st, date);
  const cd = st.cd, pp = st.pp, inPerson = st.view === 'person', count = entries.length;
  const sel = date ? gridPos(date).i : 0;
  const dist = (i: number) => Math.max(Math.abs(i % 7 - sel % 7), Math.abs(Math.floor(i / 7) - Math.floor(sel / 7)));

  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    let op = 1, tr = 'none';
    if (cd === 'in-pre') op = 0;
    else if (cd === 'in') tr = `opacity 320ms ease ${90 + dist(i) * 22}ms`;
    else if (cd === 'out') { op = 0; tr = 'opacity 200ms ease'; }
    cells.push({ x: 440 + (i % 7) * 82, y: 236 + Math.floor(i / 7) * 82, op, tr });
  }

  const rank = popRanks(dayNumber(date), count), stepIn = count > 12 ? 16 : 34, stepQuick = count > 12 ? 12 : 26;
  const layout: { x: number; y: number }[] = [];
  const squares = entries.map((entry, i): DaySquare => {
    const m = memberOf(st, entry.memberId), you = isYou(st, entry.memberId);
    const x = 440 + (i % 7) * 82, y = 236 + Math.floor(i / 7) * 82;
    const hovered = st.hoverP === entry.memberId && st.view === 'day' && !cd && !st.lv;
    let op = 1, tf = 'none', tr = '';
    layout.push({ x, y });
    if (cd === 'out') { op = 0; tr = 'opacity 180ms ease'; }
    else if (inPerson) {
      if (entry.memberId === st.person) op = 0;
      else if (pp === 'out') tr = 'opacity 280ms ease 180ms';
      else { op = 0; tr = 'opacity 220ms ease'; }
    }
    else if (st.swap === 'pre' || cd === 'in-pre') { op = 0; tf = 'scale(0.4)'; }
    else if (st.swap === 'rise') tr = pop(380 + (rank[i] ?? 0) * stepIn);
    else if (st.swap === 'quick') tr = pop(30 + (rank[i] ?? 0) * stepQuick);
    return {
      id: entry.memberId, x, y,
      outline: hovered ? color.paper : m.color,
      shadow: hovered ? shadow.tileGlow : shadow.tileGlowRest,
      filter: hovered ? 'brightness(1.07)' : 'brightness(1)',
      sheenTf: hovered ? 'translateX(110%)' : 'translateX(-110%)',
      sheenTr: hovered && !reduced ? 'transform 760ms cubic-bezier(.2,.8,.2,1)' : 'none',
      picture: pfpOfMember(st, m), op, tf,
      tr: `${tr ? `${tr}, ` : ''}outline-color 200ms ease, box-shadow 240ms ease`,
      aria: copy.day.square(m.name, kindWord(entry.post.items), you),
    };
  });

  const long = date ? dateLong(date) : '';
  const message: DayMessage | null = !loaded || count === 0 ? {
    op: cd === 'in-pre' || cd === 'out' || inPerson ? 0 : 1,
    pulsing: status === 'loading',
    text: status === 'loading' ? copy.day.loading(long) : status === 'error' ? copy.day.failed(long) : date === todayOf(st) ? copy.day.emptyToday : copy.day.empty(long),
    retry: status === 'error',
  } : null;

  return { show: st.view === 'day' || inPerson, cells, squares, message, entries, layout };
}
